import { memo } from 'react';

import styled, { css } from 'styled-components';

import FieldForm from 'components/form/FieldForm';
import { CheckBox } from 'components/form/checkbox/CheckBox';

import { dot } from 'styles/commons/dot';
import { scrollBar } from 'styles/commons/scrollBar';

/**
 * @desc 약관 동의 양식 폼
 * 이 컴포넌트는 약관 동의에 대한 양식을 렌더링하며, 체크박스를 포함합니다.
 *
 * @param {string} title - 양식의 제목.
 * @param {string} content - 약관 내용 (HTML 형식).
 * @param {boolean} checkValue - 체크박스의 체크 여부.
 * @param {function} onCheckChange - 체크박스 변경 시 호출되는 함수.
 * @param {string} checkDesc - 체크박스 옆에 표시될 설명 텍스트.
 * @param {string} className - 스타일링을 위한 추가 클래스 이름.
 * @param {string} necessary - 약관 필수 또는 선택 여부 판단
 * @param {string} label - 체크박스 레이블 (기본값: "privacy").
 *
 * @returns {JSX.Element} - 렌더링된 TermsCheckInfoForm 컴포넌트.
 */
const TermsCheckInfoForm = ({
  title,
  className,
  label = 'privacy',
  termItems = [],
  children,
}) => {
  return (
    <TermForm className={className} title={title} titleClassName="term_title">
      {termItems.length !== 0 && (
        <TermList className="none">
          {termItems.map(
            (
              {
                content,
                checkValue,
                onCheckChange,
                checkDesc,
                necessary,
                subTerms,
                subColTerms,
                onClickTerms,
                height,
                label,
                disabled,
              },
              idx
            ) => {
              return (
                // 메인 약관 영역
                <TermItem key={idx}>
                  <Term>
                    <TermCheckBox
                      onChange={onCheckChange}
                      value={checkValue}
                      label={label}
                      disabled={disabled}
                    >
                      {necessary} {checkDesc}
                    </TermCheckBox>
                    {onClickTerms && (
                      <TermView onClick={onClickTerms}>약관보기</TermView>
                    )}
                  </Term>

                  {content && (
                    <TermContent
                      className="term_content"
                      height={height}
                      dangerouslySetInnerHTML={{ __html: content }}
                    />
                  )}
                  {/* 하위 약관이 있는 경우 */}
                  {subTerms && subTerms.length > 0 && (
                    <SubTermList className="flex">
                      {subTerms.map(
                        (
                          {
                            content,
                            checkValue,
                            onCheckChange,
                            checkDesc,
                            necessary,
                            disabled,
                          },
                          subIdx
                        ) => (
                          <SubTermItem key={subIdx}>
                            <Term>
                              <TermCheckBox
                                onChange={onCheckChange}
                                value={checkValue}
                                label={checkDesc}
                                disabled={disabled}
                              >
                                {necessary} {checkDesc}
                              </TermCheckBox>
                            </Term>

                            {content && (
                              <TermContent
                                className="term_content"
                                dangerouslySetInnerHTML={{ __html: content }}
                              />
                            )}
                          </SubTermItem>
                        )
                      )}
                    </SubTermList>
                  )}
                  {/* 하위 약관 세로 */}
                  {subColTerms && subColTerms.length > 0 && (
                    <SubTermList>
                      {subColTerms.map(
                        (
                          {
                            content,
                            checkValue,
                            onCheckChange,
                            checkDesc,
                            necessary,
                            disabled,
                            onClickTerms,
                          },
                          subIdx
                        ) => (
                          <TermItem key={subIdx}>
                            <Term>
                              <TermCheckBox
                                onChange={onCheckChange}
                                value={checkValue}
                                label={checkDesc}
                                disabled={disabled}
                              >
                                {necessary} {checkDesc}
                              </TermCheckBox>
                              {onClickTerms && (
                                <TermView onClick={onClickTerms}>
                                  약관보기
                                </TermView>
                              )}
                            </Term>
                            {content && (
                              <TermContent
                                className="term_content"
                                dangerouslySetInnerHTML={{ __html: content }}
                              />
                            )}
                          </TermItem>
                        )
                      )}
                    </SubTermList>
                  )}
                </TermItem>
              );
            }
          )}
        </TermList>
      )}
    </TermForm>
  );
};

export default memo(TermsCheckInfoForm);

const TermForm = styled(FieldForm)`
  width: 100%;
  // 각 조건에 따른 스타일 처리
  ${({ className }) => TermClassName[className] || ''}
  padding-top: 24px;
  border-top: 1px solid ${({ theme }) => theme.color.gray100};
`;
export const TermList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-left: 24px;
  margin: 12px 0;

  @media screen and (max-width: 768px) {
    padding-left: 0;
    margin-top: 16px;
    padding-top: 24px;
    border-top: 1px solid ${({ theme }) => theme.color.gray100};
  }
`;
const TermItem = styled.div`
  @media screen and (max-width: 768px) {
    width: 100%;
  }
`;
const Term = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media screen and (max-width: 768px) {
    flex-wrap: wrap;
    align-items: normal;
    gap: 4px;
  }

  a {
    @media screen and (max-width: 768px) {
      margin-left: auto;
    }
  }
`;
export const TermCheckBox = styled(CheckBox)`
  margin-top: 0 !important;

  @media screen and (max-width: 768px) {
    width: 80%;
    word-wrap: break-word;
    white-space: normal;
  }

  input:checked + label:after {
    top: 1px !important;
    left: 7px !important;

    @media screen and (max-width: 768px) {
      left: 9px !important;
      top: 5px !important;
    }
  }

  [type='checkbox']:checked + label,
  [type='checkbox']:not(:checked) + label {
    @media screen and (max-width: 768px) {
      padding-left: 32px;
      font-size: 14px;
    }
  }

  label {
    color: ${({ theme }) => theme.color.gray900};

    @media screen and (max-width: 768px) {
      width: 100%;
      word-wrap: break-word;
      white-space: normal !important;
      justify-content: flex-start !important;
      line-height: 1.4 !important;
      align-items: flex-start !important;
    }

    &::before,
    &:after {
      width: 18px;
      height: 18px;
    }
    &:before {
      border-radius: 20px;
      @media screen and (max-width: 768px) {
        width: 22px !important;
        height: 22px !important;
      }
    }

    /* &:after {
      @media screen and (max-width: 768px) {
        width: 22px !important;
        height: 22px !important;
      }
    } */
  }
`;
const TermView = styled.a`
  color: ${({ theme }) => theme.color.primary};
  font-size: 14px;
  font-weight: 500;
  text-decoration: underline;

  @media screen and (max-width: 768px) {
    font-size: 13px;
  }

  &:hover {
    text-decoration: underline;
  }
`;
export const TermContent = styled.div`
  /* margin-top: 8px; */
  width: 100%;
  height: ${({ height }) => (height === undefined ? '100%' : height)};
  /* padding: 12px; */
  font-size: 15px;
  color: ${({ theme }) => theme.color.disabled};
  overflow: auto;
  ${scrollBar.default}
  @media screen and (max-width: 768px) {
    height: 400px;
  }

  /* 약관 양식 스타일 */
  .content {
    line-height: 1.6;
    strong {
      display: inline-block;
      margin-top: 8px;
      color: ${({ theme }) => theme.color.gray900};
      font-weight: 600;
    }
    ol {
      list-style: decimal;
      list-style-position: inside;
      font-weight: 600;
    }
    ul {
      li {
        &:before {
          ${dot.small}
          background: ${({ theme }) => theme.color.gray600};
        }
      }
    }
    u {
      text-decoration: auto;
      font-weight: 600;
    }
    table {
      width: 100%;
      margin: 8px 0;
      border: 1px solid ${({ theme }) => theme.color.gray200};

      th,
      td {
        padding: 8px;
        font-size: 14px;
        word-break: break-all;
        border: 1px solid ${({ theme }) => theme.color.gray200};
      }
      th {
        font-weight: 500;
      }
    }
  }
`;
const SubTermList = styled.div`
  padding-left: 24px;
  margin: 20px 0;

  @media screen and (max-width: 768px) {
    align-items: flex-start;
  }
  &.flex {
    display: flex;
  }
`;
const SubTermItem = styled.div`
  width: 25%;
`;

const TextAreaTermsCheckInfo = css`
  white-space: pre-wrap; /* 줄바꿈과 공백 유지 */
  font-size: 13px;
  line-height: 1.5;
  letter-spacing: normal;
`;

const TermClassName = {
  // 1:1 문의
  inquiry_info: css`
    width: 100%;
    .term_title {
      font-size: 18px;
      font-weight: 500;

      @media screen and (max-width: 768px) {
        font-size: 16px;
        font-weight: 600;
      }
    }
    .term_content {
      margin-top: 32px;

      @media screen and (max-width: 768px) {
        margin-top: 20px;
      }
    }
    .term_content {
      ${TextAreaTermsCheckInfo}
    }
  `,
  // 요금제 신청
  biling_type_info: css`
    .term_title,
    .term_content {
      width: 100%;
      height: 52px;
      font-size: 14px;
      vertical-align: middle;
      letter-spacing: -0.15px;
      border: 1px solid ${({ theme }) => theme.color.blueGray100};
    }
    .term_title {
      line-height: 52px;
      font-size: 14px;
      border-width: 1px 1px 0px;
      padding: 0px 20px;
      min-width: 170px;
      background: #f4f4f4;
    }
    .term_content {
      width: 1200px;
      height: 200px;
      margin-top: 0;
      font-size: 13px;
      line-height: 1.5;
      white-space: pre-wrap;
    }
  `,
  // 개인정보 약관
  signup_info: css`
    .content {
      font-size: 13px;
      padding: 25px 10px;

      & + .content {
        border-top: 1px solid ${({ theme }) => theme.color.gray200};
      }
      &:first-child {
        padding-top: 0;
      }
      &:last-child {
        padding-bottom: 0;
      }
    }

    strong {
      margin: 5px 0;
      color: ${({ theme }) => theme.color.gray700};
      font-weight: 500;
      letter-spacing: -0.015px;
      font-size: 15px;
    }

    ul,
    ol {
      margin: 16px 0 10px 24px;
      color: #666;
      list-style-type: disc;

      &.none {
        list-style-type: none;
      }
    }
    ol {
      list-style-type: decimal; /* ol의 기본값 */
    }

    p {
      color: #666;
      margin: 0;
      line-height: 30px;
    }

    .margin_left {
      margin-left: 15px;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      margin: 20px 0;

      th,
      td {
        border: 1px solid #ddd;
        padding: 10px;
        text-align: left;
        color: #666;
      }

      th {
        background-color: #f2f2f2;
        font-weight: 600;
      }
    }
  `,
  // 회원 탈퇴
  withdrawal_info: css`
    .term_content {
      ${TextAreaTermsCheckInfo}
      height: 400px;
    }
  `,
  // 마케팅 수신 동의
  marketing_info: css`
    padding-top: 5px !important;
    .none {
      padding-left: 0px;

      @media screen and (max-width: 768px) {
        border-top: 0;
        padding-top: 0;
      }
    }
  `,
};
